import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiUser, FiMail, FiGlobe, FiTwitter, FiInstagram, FiYoutube, FiEye, FiEyeOff, FiSave, FiCopy, FiCheck, FiVideo, FiExternalLink, FiX, FiSearch, FiTag, FiAlertCircle } = FiIcons

// Professional tags available for selection
const PROFESSIONAL_TAGS = [
  'Actor', 'Actress', 'Producer', 'Executive Producer', 'Writer', 'Screenwriter', 'Director', 'Assistant Director',
  'Cinematographer', 'Director of Photography', 'Editor', 'Sound Editor', 'Composer', 'Sound Designer',
  'Production Designer', 'Set Designer', 'Costume Designer', 'Makeup Artist', 'Hair Stylist',
  'Visual Effects Artist', 'VFX Supervisor', 'Animator', '3D Artist', 'Motion Graphics Artist',
  'Stunt Coordinator', 'Casting Director', 'Script Supervisor', 'Gaffer', 'Key Grip', 'Boom Operator',
  'Location Manager', 'Unit Production Manager', 'Line Producer', 'Film Distributor', 'Sales Agent',
  'Publicist', 'Marketing Manager', 'Film Critic', 'Film Journalist', 'Talent Agent', 'Entertainment Lawyer',
  'Colorist', 'Foley Artist', 'Re-recording Mixer', 'Camera Operator', 'Focus Puller', 'Dolly Grip',
  'Video Village Operator', 'DIT', 'Script Coordinator', 'Continuity', 'Wardrobe Supervisor',
  'Props Master', 'Set Decorator', 'Construction Coordinator', 'Transportation Coordinator',
  'Craft Services', 'Catering', 'Security', 'Medic', 'Photographer', 'Behind the Scenes',
  'Documentary Filmmaker', 'Commercial Director', 'Music Video Director', 'Content Creator', 'Influencer'
]

const Account = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState({
    display_name: '',
    email: '',
    bio: '',
    website: '',
    twitter: '',
    instagram: '',
    youtube: '',
    showreel_url: '',
    username: '',
    professional_tags: [],
    is_public: false,
    show_email: false,
    avatar_url: null
  })

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [autoSaving, setAutoSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [usernameAvailable, setUsernameAvailable] = useState(null)
  const [checkingUsername, setCheckingUsername] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  
  // Tags selector state
  const [tagSearch, setTagSearch] = useState('')
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [filteredTags, setFilteredTags] = useState(PROFESSIONAL_TAGS)
  const tagInputRef = useRef(null)
  const dropdownRef = useRef(null)

  // Refs for tracking
  const initialProfileRef = useRef(null)
  const autoSaveTimeoutRef = useRef(null)
  const lastSavedRef = useRef(null)

  // Load user profile on mount
  useEffect(() => {
    if (user) {
      loadUserProfile()
    }
  }, [user])

  // Smart focus handling - only reload if data hasn't changed significantly
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 Window gained focus, checking if reload needed...')
      // Only reload if we don't have unsaved changes AND it's been a while since last reload
      const now = Date.now()
      const lastReload = lastSavedRef.current || 0
      const timeSinceLastReload = now - lastReload
      
      if (user && !hasUnsavedChanges && timeSinceLastReload > 30000) { // 30 seconds
        console.log('📱 Reloading profile data after focus...')
        loadUserProfile()
      } else if (hasUnsavedChanges) {
        console.log('⚠️ Skipping reload due to unsaved changes')
      } else {
        console.log('⏰ Skipping reload - too recent')
      }
    }

    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    window.addEventListener('focus', handleFocus)
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [user, hasUnsavedChanges])

  // Auto-save draft changes (improved)
  const autoSaveDraft = useCallback(async (profileData) => {
    try {
      setAutoSaving(true)
      const { supabase } = await import('../lib/supabase')

      const userData = {
        id: user.id,
        email: user.email,
        display_name: profileData.display_name || null,
        bio: profileData.bio || null,
        website: profileData.website || null,
        twitter: profileData.twitter || null,
        instagram: profileData.instagram || null,
        youtube: profileData.youtube || null,
        showreel_url: profileData.showreel_url || null,
        username: profileData.username || null,
        professional_tags: profileData.professional_tags || [],
        is_public: profileData.is_public || false,
        show_email: profileData.show_email || false,
        avatar_url: profileData.avatar_url || null,
        updated_at: new Date()
      }

      const { error } = await supabase
        .from('users')
        .upsert(userData, { 
          onConflict: 'id',
          ignoreDuplicates: false
        })

      if (error) throw error

      console.log('📝 Auto-saved profile draft')
      setHasUnsavedChanges(false)
      lastSavedRef.current = Date.now()
    } catch (err) {
      console.error('Auto-save error:', err)
      
      // Show user-friendly error for missing columns
      if (err.message && err.message.includes('show_email')) {
        console.warn('⚠️ Database missing user profile columns. Run the database migration to fix this.')
        // Don't spam the user with alerts during auto-save, just log it
      }
    } finally {
      setAutoSaving(false)
    }
  }, [user])

  // Filter tags based on search
  useEffect(() => {
    if (tagSearch) {
      const filtered = PROFESSIONAL_TAGS.filter(tag =>
        tag.toLowerCase().includes(tagSearch.toLowerCase()) &&
        !profile.professional_tags.includes(tag)
      )
      setFilteredTags(filtered)
    } else {
      setFilteredTags(PROFESSIONAL_TAGS.filter(tag => !profile.professional_tags.includes(tag)))
    }
  }, [tagSearch, profile.professional_tags])

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagInputRef.current && dropdownRef.current) {
        if (!tagInputRef.current.contains(event.target) && 
            !dropdownRef.current.contains(event.target)) {
          setShowTagDropdown(false)
        }
      }
    }

    if (showTagDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showTagDropdown])

  // Track changes for unsaved indicator (improved)
  useEffect(() => {
    if (initialProfileRef.current) {
      const currentStr = JSON.stringify(profile)
      const initialStr = JSON.stringify(initialProfileRef.current)
      const hasChanges = currentStr !== initialStr
      
      setHasUnsavedChanges(hasChanges)

      // Auto-save after 5 seconds of no changes (increased from 3)
      if (hasChanges) {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current)
        }
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveDraft(profile)
        }, 5000) // 5 seconds
      }
    }
  }, [profile, autoSaveDraft])

  const loadUserProfile = async () => {
    try {
      setLoading(true)
      const { supabase } = await import('../lib/supabase')
      
      // Get user profile from database
      const { data: userProfile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading profile:', error)
        return
      }

      const profileData = {
        display_name: userProfile?.display_name || '',
        email: user.email, // Use auth email (always current)
        bio: userProfile?.bio || '',
        website: userProfile?.website || '',
        twitter: userProfile?.twitter || '',
        instagram: userProfile?.instagram || '',
        youtube: userProfile?.youtube || '',
        showreel_url: userProfile?.showreel_url || '',
        username: userProfile?.username || '',
        professional_tags: userProfile?.professional_tags || [],
        is_public: userProfile?.is_public || false,
        show_email: userProfile?.show_email || false,
        avatar_url: userProfile?.avatar_url || null
      }

      // Debug log for show_email setting
      console.log('📧 Email visibility setting loaded:', {
        show_email: userProfile?.show_email,
        is_public: userProfile?.is_public,
        email: user.email
      })

      setProfile(profileData)
      initialProfileRef.current = { ...profileData }
      setHasUnsavedChanges(false)
      lastSavedRef.current = Date.now()

      console.log('✅ Profile loaded successfully')
    } catch (err) {
      console.error('Error loading user profile:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    // Debug log for email visibility changes
    if (field === 'show_email') {
      console.log('📧 Email visibility toggled:', { from: profile.show_email, to: value })
    }
    
    setProfile(prev => ({
      ...prev,
      [field]: value
    }))

    // Check username availability when username changes
    if (field === 'username') {
      checkUsernameAvailability(value)
    }
  }

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null)
      return
    }

    // Clean username (alphanumeric and underscores only)
    const cleanUsername = username.toLowerCase().replace(/[^a-z0-9_]/g, '')
    if (cleanUsername !== username.toLowerCase()) {
      setProfile(prev => ({ ...prev, username: cleanUsername }))
      return
    }

    setCheckingUsername(true)
    try {
      const { supabase } = await import('../lib/supabase')
      
      // Use a simpler query to avoid RLS issues
      const { data, error } = await supabase
        .from('users')
        .select('id, username')
        .eq('username', cleanUsername)
        .limit(1)

      if (error) {
        console.error('Error checking username:', error)
        setUsernameAvailable(null)
        return
      }

      // Check if username is taken by someone else
      const isTaken = data && data.length > 0 && data[0].id !== user.id
      setUsernameAvailable(!isTaken)

    } catch (err) {
      console.error('Error checking username:', err)
      setUsernameAvailable(null)
    } finally {
      setCheckingUsername(false)
    }
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      setUploadingImage(true)
      const { supabase } = await import('../lib/supabase')

      console.log('📷 Starting image upload...', file.name)

      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/avatar.${fileExt}`
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw uploadError
      }

      console.log('✅ File uploaded successfully:', uploadData.path)

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      console.log('🔗 Public URL generated:', publicUrl)

      // Update profile state
      setProfile(prev => ({
        ...prev,
        avatar_url: publicUrl
      }))

      // Force save the avatar immediately
      const updatedProfile = { ...profile, avatar_url: publicUrl }
      await autoSaveDraft(updatedProfile)

      console.log('✅ Avatar uploaded and saved successfully')
    } catch (err) {
      console.error('Error uploading avatar:', err)
      alert('Error uploading image: ' + err.message)
    } finally {
      setUploadingImage(false)
    }
  }

  const addProfessionalTag = (tag) => {
    if (profile.professional_tags.length >= 3) {
      alert('Maximum 3 professional tags allowed')
      return
    }
    
    if (profile.professional_tags.includes(tag)) {
      return
    }
    
    // Update the profile immediately
    setProfile(prev => ({
      ...prev,
      professional_tags: [...prev.professional_tags, tag]
    }))
    
    // Clear search and close dropdown
    setTagSearch('')
    setShowTagDropdown(false)
  }

  const removeProfessionalTag = (tagToRemove) => {
    setProfile(prev => ({
      ...prev,
      professional_tags: prev.professional_tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // Clear auto-save timeout since we're doing manual save
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }

      await autoSaveDraft(profile)

      // Update the initial profile reference
      initialProfileRef.current = { ...profile }
      setHasUnsavedChanges(false)

      console.log('✅ Profile saved successfully')
      alert('Profile updated successfully!')
      
    } catch (err) {
      console.error('Error saving profile:', err)
      
      if (err.message && err.message.includes('show_email')) {
        alert('❌ Database Error: Missing user profile columns.\n\nPlease contact support or run the database migration to add the required columns (show_email, username, showreel_url, professional_tags).')
      } else {
        alert('Error saving profile. Please try again.')
      }
    } finally {
      setSaving(false)
    }
  }

  const getPublicProfileUrl = () => {
    const baseUrl = window.location.origin
    return `${baseUrl}/#/profile/${profile.username || user.id}`
  }

  const copyProfileUrl = () => {
    navigator.clipboard.writeText(getPublicProfileUrl())
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Helper function to get YouTube video ID
  const getYouTubeVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)
    return match ? match[1] : null
  }

  if (loading) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your profile...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header with Status */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
            <p className="text-gray-600 mt-1">Manage your profile and public presence</p>
          </div>
          
          {/* Status Indicators */}
          <div className="flex items-center space-x-3">
            {autoSaving && (
              <div className="flex items-center space-x-2 text-blue-600 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Auto-saving...</span>
              </div>
            )}
            
            {uploadingImage && (
              <div className="flex items-center space-x-2 text-purple-600 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                <span>Uploading image...</span>
              </div>
            )}
            
            {hasUnsavedChanges && !autoSaving && !uploadingImage && (
              <div className="flex items-center space-x-2 text-amber-600 text-sm">
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4" />
                <span>Unsaved changes</span>
              </div>
            )}
            
            {!hasUnsavedChanges && !autoSaving && !uploadingImage && !loading && (
              <div className="flex items-center space-x-2 text-green-600 text-sm">
                <SafeIcon icon={FiCheck} className="w-4 h-4" />
                <span>All changes saved</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-8 space-y-8">
          {/* Profile Image */}
          <div className="flex items-center space-x-6">
            <div className="relative">
              {profile.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt="Profile" 
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                  onError={(e) => {
                    console.error('Avatar failed to load:', profile.avatar_url)
                    e.target.style.display = 'none'
                  }}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center border-4 border-gray-100">
                  <SafeIcon icon={FiUser} className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                <SafeIcon icon={FiUser} className="w-4 h-4" />
                <input
                  type="file"
                  onChange={handleImageUpload}
                  accept="image/*"
                  disabled={uploadingImage}
                  className="hidden"
                />
              </label>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">Profile Photo</h3>
              <p className="text-sm text-gray-600">Upload a photo to personalize your profile</p>
              {uploadingImage && (
                <p className="text-xs text-blue-600 mt-1">Uploading image...</p>
              )}
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={profile.display_name}
                onChange={(e) => handleInputChange('display_name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Your display name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed here</p>
            </div>
          </div>

          {/* Username for Public Profile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username (Public Profile URL)
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 text-sm">nupitch.com/profile/</span>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={profile.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    usernameAvailable === false ? 'border-red-300 bg-red-50' : 
                    usernameAvailable === true ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  }`}
                  placeholder="your-username"
                />
                {checkingUsername && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  </div>
                )}
              </div>
            </div>
            {usernameAvailable === false && (
              <p className="text-xs text-red-600 mt-1">Username is already taken</p>
            )}
            {usernameAvailable === true && (
              <p className="text-xs text-green-600 mt-1">Username is available</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Only letters, numbers, and underscores. Minimum 3 characters.</p>
          </div>

          {/* Professional Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional Tags (Choose up to 3)
            </label>
            
            {/* Selected Tags */}
            <div className="flex flex-wrap gap-2 mb-3">
              {profile.professional_tags.map((tag) => (
                <div
                  key={tag}
                  className="flex items-center space-x-2 bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm"
                >
                  <SafeIcon icon={FiTag} className="w-3 h-3" />
                  <span>{tag}</span>
                  <button
                    onClick={() => removeProfessionalTag(tag)}
                    className="hover:bg-primary-200 rounded-full p-0.5"
                  >
                    <SafeIcon icon={FiX} className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            {/* Tag Search */}
            {profile.professional_tags.length < 3 && (
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  ref={tagInputRef}
                  type="text"
                  value={tagSearch}
                  onChange={(e) => {
                    setTagSearch(e.target.value)
                    setShowTagDropdown(true)
                  }}
                  onFocus={() => setShowTagDropdown(true)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Search for your profession..."
                />

                {/* Tag Dropdown - COMPLETELY NEW APPROACH */}
                {showTagDropdown && filteredTags.length > 0 && (
                  <div 
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                    style={{ zIndex: 1000 }}
                  >
                    <div className="p-2 text-xs text-gray-500 border-b border-gray-100">
                      Click any tag to add it to your profile:
                    </div>
                    {filteredTags.slice(0, 10).map((tag) => (
                      <div
                        key={tag}
                        className="px-4 py-3 hover:bg-primary-50 cursor-pointer border-b border-gray-50 last:border-0"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          addProfessionalTag(tag)
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault()
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <SafeIcon icon={FiTag} className="w-4 h-4 text-primary-500" />
                          <span className="text-gray-900 font-medium">{tag}</span>
                          <span className="text-xs text-gray-500 ml-auto">Click to add</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Close dropdown when clicking outside */}
            {showTagDropdown && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowTagDropdown(false)}
              />
            )}

            {profile.professional_tags.length >= 3 && (
              <p className="text-xs text-amber-600 mt-1">Maximum 3 professional tags selected</p>
            )}
            
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              value={profile.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={3}
              placeholder="Tell people about yourself and your work..."
            />
          </div>

          {/* Showreel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Showreel (YouTube URL)
            </label>
            <div className="relative">
              <SafeIcon icon={FiVideo} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="url"
                value={profile.showreel_url}
                onChange={(e) => handleInputChange('showreel_url', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Your showreel will be displayed on your public profile</p>
            
            {/* Showreel Preview */}
            {profile.showreel_url && getYouTubeVideoId(profile.showreel_url) && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Showreel Preview:</p>
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(profile.showreel_url)}`}
                    title="Showreel Preview"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Social Links</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <div className="relative">
                  <SafeIcon icon={FiGlobe} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="url"
                    value={profile.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Twitter
                </label>
                <div className="relative">
                  <SafeIcon icon={FiTwitter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={profile.twitter}
                    onChange={(e) => handleInputChange('twitter', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="@username or full URL"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram
                </label>
                <div className="relative">
                  <SafeIcon icon={FiInstagram} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={profile.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="@username or full URL"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  YouTube
                </label>
                <div className="relative">
                  <SafeIcon icon={FiYoutube} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={profile.youtube}
                    onChange={(e) => handleInputChange('youtube', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Channel URL"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Privacy & Public Profile */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Privacy Settings</h3>
            
            <div className="space-y-4">
              {/* Public Profile Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <SafeIcon 
                    icon={profile.is_public ? FiEye : FiEyeOff} 
                    className="w-5 h-5 text-gray-600" 
                  />
                  <div>
                    <p className="font-medium text-gray-900">Public Profile</p>
                    <p className="text-sm text-gray-600">
                      {profile.is_public 
                        ? 'Your profile is visible to everyone with the link'
                        : 'Your profile is private and only visible to you'
                      }
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => handleInputChange('is_public', !profile.is_public)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    profile.is_public ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      profile.is_public ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Email Visibility Toggle */}
              {profile.is_public && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <SafeIcon 
                      icon={FiMail} 
                      className="w-5 h-5 text-gray-600" 
                    />
                    <div>
                      <p className="font-medium text-gray-900">Show Email on Public Profile</p>
                      <p className="text-sm text-gray-600">
                        {profile.show_email 
                          ? 'Your email will be visible on your public profile'
                          : 'Your email will be hidden from your public profile'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleInputChange('show_email', !profile.show_email)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      profile.show_email ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        profile.show_email ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Public Profile URL */}
              {profile.is_public && profile.username && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-900">Your Public Profile URL</p>
                      <p className="text-blue-700 text-sm break-all">{getPublicProfileUrl()}</p>
                    </div>
                    <button
                      onClick={copyProfileUrl}
                      className="ml-4 flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
                      <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                  <p className="text-blue-600 text-xs mt-2">
                    Share this link to let people view your profile and projects
                  </p>
                </div>
              )}

              {profile.is_public && !profile.username && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-yellow-800 text-sm">
                    <strong>Set a username</strong> to get a custom profile URL that's easy to share
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving || uploadingImage || (profile.username && usernameAvailable === false)}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiSave} className="w-5 h-5" />
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Account